import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import {
  Phone, ChatCircleDots, Fire, Warning, Trophy, ArrowClockwise,
  User, ClockCountdown, Buildings, XCircle, CheckCircle, Star,
  MoonStars, PhoneSlash,
} from "@phosphor-icons/react";

/**
 * Phase 4 — Admin "Aujourd'hui" dashboard.
 *
 * Mobile-first : big touch targets, single column on <640px, 2 columns
 * on tablet, per-télépro cards horizontally scrollable on very narrow
 * viewports. Load time budget < 10 s (backend agg endpoint is < 2 s).
 *
 * data-testids : today-kpis, today-telepro-card-{code},
 * today-opportunities, today-issues.
 */
export default function TodayPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/telepro/dashboard-today");
      setData(r.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Chargement impossible");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => {
    load();
    // Live refresh every 90 s for the "watch the day unfold" experience
    const iv = setInterval(load, 90 * 1000);
    return () => clearInterval(iv);
  }, []);

  const refresh = () => { setRefreshing(true); load(); };

  const [summarySending, setSummarySending] = useState(false);
  const [summaryMsg, setSummaryMsg] = useState("");
  const sendSummaryNow = async () => {
    if (!window.confirm("Envoyer le bilan admin par email maintenant ? (PDF joint)"))
      return;
    setSummarySending(true);
    setSummaryMsg("");
    try {
      const r = await api.post("/admin/telepro/daily-summary/send?force=true");
      setSummaryMsg(r.data?.sent
        ? `✅ Envoyé à ${r.data.to}`
        : `⚠ ${r.data?.reason || "Non envoyé"}`);
    } catch (e) {
      setSummaryMsg(`❌ ${e?.response?.data?.detail || "Envoi impossible"}`);
    } finally {
      setSummarySending(false);
      setTimeout(() => setSummaryMsg(""), 6000);
    }
  };
  const downloadSummaryPdf = () => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("mc68_token") || "";
    // Use fetch to include the Authorization header, then trigger a blob download
    fetch(`${base}/api/admin/telepro/daily-summary.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `synthese-teleprospection-${data?.date || "aujourdhui"}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((e) => setSummaryMsg(`❌ ${e.message}`));
  };

  // Vague 5 — comparatif ancien vs nouveau scoring pour une date donnée
  const [comparisonDate, setComparisonDate] = useState("2026-09-10");
  const downloadComparisonPdf = () => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("mc68_token") || "";
    const d = (comparisonDate || "").trim();
    if (!d) { setSummaryMsg("❌ Choisis une date"); return; }
    fetch(`${base}/api/admin/telepro/scoring-comparison.pdf?date=${encodeURIComponent(d)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `comparatif-scoring-${d}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        setSummaryMsg(`✅ Rapport comparatif ${d} téléchargé`);
        setTimeout(() => setSummaryMsg(""), 5000);
      })
      .catch((e) => setSummaryMsg(`❌ ${e.message}`));
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">Chargement du dashboard…</div>;
  }
  if (error) {
    return <div className="text-sm text-secondary bg-secondary/10 border border-secondary/30 rounded-sm p-3">{error}</div>;
  }
  if (!data) return null;

  const k = data.kpis || {};
  return (
    <div className="space-y-4" data-testid="today-panel">
      {/* HERO — top KPIs */}
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <div className="label-uppercase text-primary">🎯 Aujourd'hui</div>
          <div className="font-display text-xl font-medium tabular-nums">{data.date}</div>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="text-[11px] font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1 border border-border rounded-sm px-2 py-1"
          data-testid="today-refresh-btn"
        >
          <ArrowClockwise size={11} weight="bold" className={refreshing ? "animate-spin" : ""}/> Rafraîchir
        </button>
      </div>

      {/* Phase 5 — Admin daily summary quick actions */}
      <div className="flex flex-wrap items-center gap-2" data-testid="daily-summary-actions">
        <button
          onClick={downloadSummaryPdf}
          className="text-[11px] font-semibold text-primary hover:bg-primary/10 inline-flex items-center gap-1 border border-primary/40 rounded-sm px-2 py-1"
          data-testid="download-daily-summary-pdf"
        >
          📄 Télécharger la synthèse PDF
        </button>
        <button
          onClick={sendSummaryNow}
          disabled={summarySending}
          className="text-[11px] font-semibold text-primary hover:bg-primary/10 inline-flex items-center gap-1 border border-primary/40 rounded-sm px-2 py-1 disabled:opacity-50"
          data-testid="send-daily-summary-now"
        >
          {summarySending ? "Envoi…" : "✉ M'envoyer le bilan par email"}
        </button>
        {/* Vague 5 — Comparatif ancien vs nouveau scoring */}
        <div className="inline-flex items-center gap-1 border border-secondary/40 rounded-sm px-2 py-1" data-testid="scoring-comparison-bar">
          <span className="text-[10px] uppercase tracking-wider font-bold text-secondary">📊 Comparatif V1/V2</span>
          <input
            type="date"
            value={comparisonDate}
            onChange={(e) => setComparisonDate(e.target.value)}
            className="text-[11px] bg-transparent border-none focus:outline-none tabular-nums"
            data-testid="scoring-comparison-date"
          />
          <button
            onClick={downloadComparisonPdf}
            className="text-[11px] font-semibold text-secondary hover:underline"
            data-testid="download-scoring-comparison-pdf"
          >
            → PDF
          </button>
        </div>
        {summaryMsg && (
          <span className="text-[11px] text-muted-foreground" data-testid="daily-summary-status">
            {summaryMsg}
          </span>
        )}
      </div>

      {data.is_sunday && (
        <div
          className="bg-amber-500/10 border-l-4 border-amber-500 rounded-sm px-3 py-2 text-xs flex items-center gap-2"
          data-testid="today-sunday-banner"
        >
          🌴 <b className="text-amber-800">Dimanche</b>
          <span className="text-amber-700/80">Jour de repos — aucune liste générée, aucune alerte d'inactivité levée.</span>
        </div>
      )}

      {/* Flow prospects — Vague 4 : demandés vs attribués vs traités */}
      <div className="grid grid-cols-3 gap-2 bg-primary/5 border border-primary/30 rounded-sm p-2" data-testid="today-prospects-flow">
        <FlowCell label="Demandés" value={k.prospects_requested || 0} testid="flow-requested"/>
        <FlowCell label="Attribués" value={k.prospects_attributed || 0} testid="flow-attributed"/>
        <FlowCell label="Traités" value={k.prospects_treated || 0} accent testid="flow-treated"/>
      </div>

      {/* KPI grid — 2 cols mobile, 4 cols sm+ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="today-kpis">
        <KpiBig
          icon={<User size={18} weight="duotone"/>}
          label="Actifs"
          value={k.active_telepros || 0}
          sub={k.absent_telepros ? `${k.absent_telepros} absent(s)` : "Toute la team"}
          testid="kpi-active-telepros"
        />
        <KpiBig
          icon={<Phone size={18} weight="duotone"/>}
          label="Appels"
          value={k.total_calls || 0}
          sub={`${k.contacts_reached || 0} contacts humains`}
          accent="primary"
          testid="kpi-total-calls"
        />
        <KpiBig
          icon={<Fire size={18} weight="fill"/>}
          label="Opportunités"
          value={k.opportunities || 0}
          sub="RDV + intéressés + rappels"
          accent="secondary"
          testid="kpi-opportunities"
        />
        <KpiBig
          icon={<Trophy size={18} weight="duotone"/>}
          label="Ventes"
          value={k.sales || 0}
          sub={k.sales ? "🎉 Bravo !" : "En attente…"}
          accent="gold"
          testid="kpi-sales"
        />
      </div>

      {/* Secondary KPIs — quality signals from field feedback */}
      <div className="grid grid-cols-3 gap-2 text-[11px]" data-testid="today-quality-signals">
        <MiniKpi label="Faux numéros" value={k.wrong_numbers || 0} tone="warn"/>
        <MiniKpi label="Fermés signalés" value={k.closed || 0} tone="warn"/>
        <MiniKpi label="Score moy. liste" value={`${k.avg_quality_score || 0}/100`} tone="info"/>
      </div>

      {/* Per-télépro cards */}
      <div className="mt-4">
        <div className="font-display text-base font-medium mb-2 flex items-center gap-1.5">
          <Buildings size={14} weight="duotone" className="text-primary"/> Équipe téléprospection ({data.telepros.length})
        </div>
        {data.telepros.length === 0 ? (
          <div className="text-xs text-muted-foreground bg-muted/30 border border-dashed border-border rounded-sm p-4 text-center">
            Aucun télépro actif. Invite-en depuis l'onglet Téléprospection.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="today-telepro-cards">
            {data.telepros.map((tp) => <TeleproCard key={tp.code} tp={tp}/>)}
          </div>
        )}
      </div>

      {/* Opportunities section */}
      <section className="mt-6" data-testid="today-opportunities">
        <div className="font-display text-base font-medium mb-2 flex items-center gap-1.5">
          <Fire size={14} weight="fill" className="text-secondary"/> Opportunités des 24 h ({data.opportunities.length})
        </div>
        {data.opportunities.length === 0 ? (
          <div className="text-xs text-muted-foreground bg-muted/30 border border-dashed border-border rounded-sm p-4 text-center">
            Aucune opportunité créée dans les 24 dernières heures — la team démarche fort ou la journée commence à peine.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {data.opportunities.slice(0, 15).map((o, i) => <OpportunityRow key={i} o={o}/>)}
          </ul>
        )}
      </section>

      {/* Callbacks scheduled — Vague 4 (rappels programmés séparés des opportunités) */}
      {(data.callbacks_scheduled?.length ?? 0) > 0 && (
        <section className="mt-6" data-testid="today-callbacks">
          <div className="font-display text-base font-medium mb-2 flex items-center gap-1.5">
            <ClockCountdown size={14} weight="fill" className="text-[#8a6a14]"/> Rappels programmés ({data.callbacks_scheduled.length})
          </div>
          <ul className="space-y-1.5">
            {data.callbacks_scheduled.slice(0, 15).map((cb, i) => (
              <CallbackRow key={i} cb={cb}/>
            ))}
          </ul>
        </section>
      )}

      {/* Issues section */}
      <section className="mt-6" data-testid="today-issues">
        <div className="font-display text-base font-medium mb-2 flex items-center gap-1.5">
          <Warning size={14} weight="fill" className="text-orange-500"/> À surveiller ({data.issues.length})
        </div>
        {data.issues.length === 0 ? (
          <div className="text-xs text-muted-foreground bg-primary/5 border border-primary/20 rounded-sm p-4 text-center">
            🎉 Aucun problème détecté aujourd'hui. La team roule.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {data.issues.map((iss, i) => <IssueRow key={i} iss={iss}/>)}
          </ul>
        )}
      </section>
    </div>
  );
}

/* --------------- sub-components --------------- */
function KpiBig({ icon, label, value, sub, accent, testid }) {
  const cls = accent === "primary" ? "bg-primary/5 border-primary/30" :
              accent === "secondary" ? "bg-secondary/5 border-secondary/30" :
              accent === "gold" ? "bg-amber-500/10 border-amber-500/40" :
              "bg-card border-border";
  return (
    <div className={`border rounded-sm p-3 ${cls}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">
        {icon} {label}
      </div>
      <div className="font-display text-2xl font-medium tabular-nums leading-none">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function MiniKpi({ label, value, tone }) {
  const cls = tone === "warn" ? "bg-orange-500/10 border-orange-500/30 text-orange-700" :
              tone === "info" ? "bg-primary/5 border-primary/30 text-primary" :
              "bg-card border-border";
  return (
    <div className={`border rounded-sm px-2 py-1.5 text-center ${cls}`}>
      <div className="tabular-nums font-bold text-sm">{value}</div>
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
    </div>
  );
}

function TeleproCard({ tp }) {
  const c = tp.commercial || {};
  const q = tp.list_quality || {};
  const s = tp.session || {};
  const totalPerf = (c.callbacks || 0) + (c.interested || 0) + (c.meetings || 0) + (c.site_web_besoin || 0) + (c.sales || 0);
  return (
    <div
      className={`border rounded-sm p-3 ${tp.absent ? "bg-muted/30 border-dashed" : "bg-card border-border"}`}
      data-testid={`today-telepro-card-${tp.code}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-display text-sm font-semibold truncate">{tp.name}</div>
          <div className="text-[10px] font-mono text-muted-foreground">{tp.code}</div>
        </div>
        {tp.absent ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-muted-foreground bg-muted/50 border border-border rounded-sm px-1.5 py-0.5">
            <MoonStars size={10}/> Absent
          </span>
        ) : s.active ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-primary bg-primary/10 border border-primary/40 rounded-sm px-1.5 py-0.5" title={`Session ouverte · ${s.calls_count || 0} appels`}>
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"/> En ligne
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-muted-foreground bg-muted/30 border border-border rounded-sm px-1.5 py-0.5">
            Hors ligne
          </span>
        )}
      </div>

      {/* COMMERCIAL — what the télépro DID */}
      <div className="bg-primary/5 border border-primary/20 rounded-sm p-2 mb-2">
        <div className="text-[10px] uppercase tracking-wider font-bold text-primary mb-1 flex items-center gap-1">
          <ChatCircleDots size={10} weight="fill"/> Perf commerciale
        </div>
        <div className="grid grid-cols-4 gap-1 text-center">
          <PerfCell value={c.calls || 0} label="Appels"/>
          <PerfCell value={c.contacts_reached || 0} label="Contacts"/>
          <PerfCell value={totalPerf} label="Opp." tone="secondary"/>
          <PerfCell value={c.sales || 0} label="Ventes" tone="gold"/>
        </div>
      </div>

      {/* LIST QUALITY — what the télépro RECEIVED */}
      <div className="bg-muted/30 border border-border rounded-sm p-2">
        <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1 flex items-center gap-1">
          <Star size={10} weight="fill"/> Qualité liste ({q.total || 0} fiches · moy. {q.avg_score || 0}/100)
        </div>
        {/* Tier bar */}
        <div className="flex h-1.5 rounded-full overflow-hidden bg-background mb-1">
          <TierChunk v={q.excellent} total={q.total} cls="bg-primary"/>
          <TierChunk v={q.bon} total={q.total} cls="bg-emerald-500"/>
          <TierChunk v={q.moyen} total={q.total} cls="bg-amber-500"/>
          <TierChunk v={q.faible} total={q.total} cls="bg-orange-500"/>
          <TierChunk v={q.exclure} total={q.total} cls="bg-secondary"/>
        </div>
        {(q.wrong_numbers > 0 || q.closed > 0 || q.refused_final > 0) && (
          <div className="flex flex-wrap gap-1.5 text-[10px] mt-1">
            {q.wrong_numbers > 0 && (
              <span className="inline-flex items-center gap-0.5 bg-orange-500/10 text-orange-700 border border-orange-500/30 rounded-sm px-1.5 py-0.5">
                <PhoneSlash size={9} weight="bold"/> {q.wrong_numbers} faux num.
              </span>
            )}
            {q.closed > 0 && (
              <span className="inline-flex items-center gap-0.5 bg-secondary/10 text-secondary border border-secondary/30 rounded-sm px-1.5 py-0.5">
                <XCircle size={9} weight="bold"/> {q.closed} fermé(s)
              </span>
            )}
            {q.refused_final > 0 && (
              <span className="inline-flex items-center gap-0.5 bg-secondary/10 text-secondary border border-secondary/30 rounded-sm px-1.5 py-0.5">
                <XCircle size={9} weight="bold"/> {q.refused_final} refus final
              </span>
            )}
          </div>
        )}
      </div>

      {s.active && s.untreated > 0 && (
        <div className="mt-2 text-[10px] text-muted-foreground flex items-center gap-1">
          <ClockCountdown size={10} weight="duotone"/> Reste <b className="tabular-nums text-foreground">{s.untreated}</b> prospect(s) à traiter
        </div>
      )}
    </div>
  );
}

function PerfCell({ value, label, tone }) {
  const cls = tone === "gold" ? "text-amber-600" :
              tone === "secondary" ? "text-secondary" :
              "text-foreground";
  return (
    <div>
      <div className={`font-display text-lg font-semibold tabular-nums ${cls}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function TierChunk({ v, total, cls }) {
  const pct = total ? (v / total) * 100 : 0;
  if (!pct) return null;
  return <div className={`${cls} h-full`} style={{ width: `${pct}%` }}/>;
}

const KIND_LABEL = {
  interested: "Intéressé",
  meeting: "RDV",
  callback: "Rappel",
  site_web_besoin: "Site web 🌐",
};
function OpportunityRow({ o }) {
  const dt = o.at ? new Date(o.at) : null;
  const timeStr = dt ? dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <li className="flex items-start gap-2 text-xs bg-secondary/5 border-l-4 border-secondary/40 rounded-sm px-2.5 py-1.5" data-testid={`opportunity-${o.business_id}`}>
      <span className="shrink-0 bg-secondary text-secondary-foreground text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-sm">
        {KIND_LABEL[o.kind] || o.kind}
      </span>
      <div className="flex-1 min-w-0">
        <div className="truncate"><b className="text-foreground">{o.business_name}</b> {o.city && <span className="text-muted-foreground">· {o.city}</span>}</div>
        {o.note && <div className="text-[11px] text-muted-foreground italic truncate">« {o.note} »</div>}
      </div>
      <div className="text-[10px] text-muted-foreground shrink-0 text-right">
        <div className="tabular-nums">{timeStr}</div>
        <div className="font-mono">{o.telepro_code}</div>
      </div>
    </li>
  );
}

const SEVERITY_CLS = {
  high:   "bg-secondary/10 border-secondary/40 text-secondary",
  medium: "bg-orange-500/10 border-orange-500/30 text-orange-700",
  info:   "bg-muted/40 border-border text-muted-foreground",
};
function IssueRow({ iss }) {
  const cls = SEVERITY_CLS[iss.severity] || SEVERITY_CLS.info;
  return (
    <li className={`flex items-start gap-2 text-xs border-l-4 rounded-sm px-2.5 py-1.5 ${cls}`} data-testid={`issue-${iss.kind}-${iss.telepro_code}`}>
      {iss.severity === "high" ? <Warning size={12} weight="fill" className="shrink-0 mt-0.5"/> : <CheckCircle size={12} weight="bold" className="shrink-0 mt-0.5 opacity-60"/>}
      <div className="flex-1 min-w-0">
        <div className="truncate"><b className="font-mono">{iss.telepro_code}</b> — {iss.detail}</div>
      </div>
    </li>
  );
}

function FlowCell({ label, value, accent, testid }) {
  return (
    <div
      className={`text-center rounded-sm px-2 py-2 border ${accent ? "bg-primary text-primary-foreground border-primary" : "bg-background border-primary/20"}`}
      data-testid={testid}
    >
      <div className={`text-[9px] uppercase tracking-wider font-bold ${accent ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{label}</div>
      <div className="font-display text-xl font-medium tabular-nums leading-none mt-0.5">{value}</div>
    </div>
  );
}

function CallbackRow({ cb }) {
  const dt = cb.callback_at ? new Date(cb.callback_at) : null;
  const dateStr = dt ? dt.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "?";
  return (
    <li
      className="flex items-start gap-2 text-xs bg-[#E0B043]/10 border-l-4 border-[#E0B043]/60 rounded-sm px-2.5 py-1.5"
      data-testid={`callback-${cb.business_id}`}
    >
      <span className="shrink-0 bg-[#8a6a14] text-white text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-sm tabular-nums">
        {dateStr}
      </span>
      <div className="flex-1 min-w-0">
        <div className="truncate"><b className="text-foreground">{cb.business_name}</b>
          {cb.city && <span className="text-muted-foreground"> · {cb.city}</span>}
          {cb.phone && <span className="ml-1 font-mono text-primary/80">· {cb.phone}</span>}
        </div>
        {(cb.callback_reason || cb.note) && (
          <div className="text-[11px] text-muted-foreground italic truncate">« {cb.callback_reason || cb.note} »</div>
        )}
      </div>
      <div className="text-[10px] text-muted-foreground shrink-0 font-mono">{cb.telepro_code}</div>
    </li>
  );
}

